import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, User, MapPin } from "lucide-react";
import { toast } from "sonner";
import { es } from "date-fns/locale";
import api, { money, fmtDate, formatApiError, todayISO } from "@/lib/api";
import { JOB_STATUSES } from "@/lib/constants";
import { useApp } from "@/context/AppContext";
import { PageHeader, StatusBadge, ConfirmDelete, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import JobMaterials from "@/components/JobMaterials";

const blank = () => ({ title: "", client_name: "", client_phone: "", address: "", start_date: todayISO(), due_date: "", status: "pendiente", assigned_to: "", amount: 0, notes: "", materials: [] });
const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function JobDialog({ job, onClose, onSaved }) {
  const [f, setF] = useState(job);
  useEffect(() => { setF(job ? { ...job, materials: job.materials || [] } : job); }, [job]);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...f, amount: Number(f.amount || 0), materials: f.materials.map(({ material_id, quantity }) => ({ material_id, quantity })) };
      if (f.id) await api.put(`/jobs/${f.id}`, body); else await api.post("/jobs", body);
      toast.success(f.materials.length || job?.materials?.length ? "Trabajo guardado y stock actualizado" : "Trabajo guardado");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={!!job} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Editar trabajo" : "Nuevo trabajo"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2" data-testid="job-form">
          <Field label="Trabajo *" className="sm:col-span-2"><Input required value={f.title} onChange={set("title")} placeholder="Ej. Cocina integral en nogal" data-testid="job-title-input" /></Field>
          <Field label="Cliente"><Input value={f.client_name} onChange={set("client_name")} data-testid="job-client-input" /></Field>
          <Field label="Teléfono"><Input value={f.client_phone} onChange={set("client_phone")} data-testid="job-phone-input" /></Field>
          <Field label="Dirección / lugar" className="sm:col-span-2"><Input value={f.address} onChange={set("address")} data-testid="job-address-input" /></Field>
          <Field label="Fecha de inicio *"><Input type="date" required value={f.start_date} onChange={set("start_date")} data-testid="job-start-input" /></Field>
          <Field label="Fecha de entrega"><Input type="date" value={f.due_date} onChange={set("due_date")} data-testid="job-due-input" /></Field>
          <Field label="Responsable"><Input value={f.assigned_to} onChange={set("assigned_to")} data-testid="job-assigned-input" /></Field>
          <Field label="Monto"><Input type="number" step="any" min="0" value={f.amount} onChange={set("amount")} data-testid="job-amount-input" /></Field>
          <Field label="Estado">
            <Select value={f.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="job-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{JOB_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2"><JobMaterials value={f.materials} original={job?.materials} onChange={(materials) => setF({ ...f, materials })} /></div>
          <Field label="Notas" className="sm:col-span-2"><Textarea rows={3} value={f.notes} onChange={set("notes")} data-testid="job-notes-input" /></Field>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="job-cancel-button">Cancelar</Button>
            <Button type="submit" className="bg-amber-700 hover:bg-amber-800" data-testid="job-save-button">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function JobCard({ j, sym, onEdit, onStatus, onDelete }) {
  return (
    <div className="rounded-xl border bg-white p-4 fade-up" data-testid={`job-card-${j.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold">{j.title}</p>
          <p className="text-xs text-stone-500 font-mono mt-0.5">{fmtDate(j.start_date)} → {fmtDate(j.due_date)}</p>
        </div>
        <StatusBadge status={j.status} />
      </div>
      <div className="mt-3 space-y-1 text-sm text-stone-600">
        {j.client_name && <p className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{j.client_name} {j.client_phone && `· ${j.client_phone}`}</p>}
        {j.address && <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{j.address}</p>}
        {j.assigned_to && <p className="text-xs">Responsable: <b>{j.assigned_to}</b></p>}
        {j.amount > 0 && <p className="font-mono text-sm">{money(j.amount, sym)}</p>}
        {j.materials?.length > 0 && (
          <p className="text-xs text-stone-500" data-testid={`job-materials-summary-${j.id}`}>
            Material: {j.materials.map((m) => `${m.name} (${m.quantity} ${m.unit})`).join(", ")}
          </p>
        )}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Select value={j.status} onValueChange={(v) => onStatus(j, v)}>
          <SelectTrigger className="h-8 text-xs flex-1" data-testid={`job-quick-status-${j.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{JOB_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="ghost" size="icon" onClick={() => onEdit(j)} data-testid={`job-edit-${j.id}`}><Pencil className="h-4 w-4" /></Button>
        <ConfirmDelete onConfirm={() => onDelete(j)} testId={`job-delete-${j.id}`} label="este trabajo" />
      </div>
    </div>
  );
}

export default function Jobs() {
  const { settings } = useApp();
  const sym = settings?.currency_symbol || "$";
  const [jobs, setJobs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [day, setDay] = useState(new Date());

  const load = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => { load(); }, []);

  const onStatus = async (j, status) => { await api.patch(`/jobs/${j.id}/status`, { status }); load(); };
  const onDelete = async (j) => { await api.delete(`/jobs/${j.id}`); toast.success("Trabajo eliminado"); load(); };
  const cardProps = { sym, onEdit: setEditing, onStatus, onDelete };

  const busyDays = useMemo(() => jobs.filter((j) => j.status !== "entregado").map((j) => new Date(`${j.start_date}T12:00:00`)), [jobs]);
  const dueDays = useMemo(() => jobs.filter((j) => j.due_date && j.status !== "entregado").map((j) => new Date(`${j.due_date}T12:00:00`)), [jobs]);
  const dayIso = day ? isoOf(day) : "";
  const dayJobs = jobs.filter((j) => j.start_date <= dayIso && (j.due_date || j.start_date) >= dayIso);
  const listed = filter === "all" ? jobs : jobs.filter((j) => j.status === filter);

  return (
    <div>
      <PageHeader title="Agenda de trabajos" subtitle={`${jobs.length} trabajos registrados`}>
        <Button className="bg-amber-700 hover:bg-amber-800" onClick={() => setEditing(blank())} data-testid="new-job-button"><Plus className="h-4 w-4 mr-1" />Nuevo trabajo</Button>
      </PageHeader>
      <Tabs defaultValue="list">
        <TabsList className="mb-5">
          <TabsTrigger value="list" data-testid="jobs-tab-list">Lista</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="jobs-tab-calendar">Calendario</TabsTrigger>
        </TabsList>
        <TabsContent value="list">
          <div className="flex flex-wrap gap-2 mb-5">
            {[{ v: "all", l: "Todos" }, ...JOB_STATUSES].map((s) => (
              <button key={s.v} onClick={() => setFilter(s.v)} data-testid={`jobs-filter-${s.v}`}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${filter === s.v ? "bg-[#1E1B18] text-amber-400 border-[#1E1B18]" : "bg-white hover:bg-stone-100"}`}>
                {s.l}
              </button>
            ))}
          </div>
          {listed.length === 0 && <p className="text-center text-stone-400 py-16">No hay trabajos en esta vista</p>}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{listed.map((j) => <JobCard key={j.id} j={j} {...cardProps} />)}</div>
        </TabsContent>
        <TabsContent value="calendar">
          <div className="grid gap-6 lg:grid-cols-[auto_1fr]">
            <div className="rounded-xl border bg-white p-3 w-fit" data-testid="jobs-calendar">
              <Calendar locale={es} mode="single" selected={day} onSelect={setDay}
                modifiers={{ busy: busyDays, due: dueDays }}
                modifiersClassNames={{ busy: "font-bold text-amber-800 underline decoration-amber-500 decoration-2", due: "bg-red-50 text-red-700" }} />
              <p className="px-3 pb-2 text-[11px] text-stone-500">Subrayado: inicio · Rojo: entrega</p>
            </div>
            <div>
              <h3 className="font-bold mb-3">Trabajos del {dayIso ? fmtDate(dayIso) : "—"}</h3>
              {dayJobs.length === 0 && <p className="text-stone-400 text-sm">Sin trabajos este día</p>}
              <div className="grid gap-4 sm:grid-cols-2">{dayJobs.map((j) => <JobCard key={j.id} j={j} {...cardProps} />)}</div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      <JobDialog job={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
